import { Icon } from '@gs/fe-storybook-gasco';
import React, { useEffect, useRef, useState } from 'react';
import { resendCode } from '../../../../config/services/steps/resendCode';
import { CodeInputProps } from '../../../../interfaces/interfaceSteps';
import { eventEmitter } from './EventEmitter';
import './CodeInput.css';

const CodeInput: React.FC<CodeInputProps> = ({
  value,
  onChange,
  placeholders,
  isCodeIncorrect,
  phoneNumber,
}) => {
  const [inputValues, setInputValues] = useState<string[]>(Array(6).fill(''));
  const [counter, setCounter] = useState(30);
  const [hasError, setHasError] = useState(isCodeIncorrect);
  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    eventEmitter.emit<boolean>('isResendingChange', isResending);
  }, [isResending]);



  useEffect(() => {
    if (isCodeIncorrect) {
      setCounter(0);
      setHasError(true);
      setIsResendEnabled(true);
    } else {
      setHasError(false);
    }
  }, [isCodeIncorrect]);

  useEffect(() => {
    if (!hasError && counter > 0) {
      intervalRef.current = setInterval(() => {
        setCounter((prevCounter) => prevCounter - 1);
      }, 1000);
    } else if (counter === 0) {
      setIsResendEnabled(true);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [counter, hasError]);

  const handleInputChange = (index: number, newValue: string) => {
    if (/^\d?$/.test(newValue)) {
      const newValues = [...inputValues];
      newValues[index] = newValue;
      setInputValues(newValues);
      onChange(newValues);

      if (newValue && index < inputValues.length - 1) {
        const nextInput = document.querySelector(
          `input[data-index="${index + 1}"]`
        ) as HTMLInputElement;
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' && !inputValues[index] && index > 0) {
      const prevInput = document.querySelector(
        `input[data-index="${index - 1}"]`
      ) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handleResendCodeWithError = async () => {
    setInputValues(Array(6).fill(''));
    setHasError(false);
    setCounter(30);
    setIsResendEnabled(false);
    setIsResending(true);
    eventEmitter.emit('isResendingChange', true);
    try {
      await resendCode(phoneNumber);
    } catch (error) {
      console.error('Error al reenviar:', error);
    }
  };

  const handleResendCode = async () => {
    setCounter(30);
    setIsResendEnabled(false);
    setIsResending(true);
    eventEmitter.emit('isResendingChange', true);
    try {
       await resendCode(phoneNumber);
    } catch (error) {
      console.error('Error al reenviar:', error);
      setErrorMessage(
        'Actualmente estamos presentando intermitencias de envíos de nuestros SMS.'
      );
    }
  };

  const handleResendClick = () => {
    if (isResendEnabled) {
      return hasError ? handleResendCodeWithError : handleResendCode;
    }
    return undefined;
  };

  return (
    <>
      {errorMessage && (
        <div className="custom-alert">
          <Icon className="exclamation-icon" name="exclamation-circle" />
          {errorMessage}
        </div>
      )}
      <div className={`code-inputs ${hasError ? "error" : ""}`} id="page-code" data-page-id="codigo">
        {Array(6)
          .fill(null)
          .map((_, index) => (
            <input
              id="codeInput"
              className={`code-number ${hasError ? "error" : ""}`}
              key={index}
              type="number"
              placeholder={placeholders.code}
              value={inputValues[index]}
              data-index={index}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              maxLength={1}
              onFocus={(e) => e.target.select()}
            />
          ))}
      </div>
      <div>
        <div className="counter">00:{String(counter).padStart(2, "0")}</div>
        <p className={`code-text ${hasError ? "error" : ""}`}>
          {hasError ? "Código incorrecto" : "¿No recibiste el código?"}{" "}
          <span
            className={`resend ${isResendEnabled ? "" : "disabled"}`}
            onClick={handleResendClick()}
          >
            Enviar nuevamente
          </span>
        </p>
      </div>
    </>
  );
};

export default CodeInput;
